import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { 
    ClipboardList, FileSignature, ShieldAlert, Users, Clock, 
    ChevronDown, ChevronRight, Download, Check, FileText, 
    Link as LinkIcon, CheckCircle2, AlertTriangle, Calendar,
    UserCog // Added for Engineer Icon
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const OwnerOverview = ({ onNavigate, currentUser }) => {
    // --- GLOBAL DATA STATE ---
    const [stats, setStats] = useState({ logs: [], permits: [], safety: [] });
    const [loading, setLoading] = useState(true);
    
    // --- LOG FILTER STATE ---
    const [logTab, setLogTab] = useState('today'); // 'today', 'history'
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const [filterMyLogs, setFilterMyLogs] = useState(false);
    
    // Updated History Filter to defaults
    const [historyFilter, setHistoryFilter] = useState({ company: '', date_from: '', date_to: '' });
    
    // --- UI STATE ---
    const [expandedSection, setExpandedSection] = useState('logs'); 
    const [selectedLog, setSelectedLog] = useState(null); 
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        loadData();
        return () => window.removeEventListener('resize', handleResize);
    }, [filterDate, logTab]);

    const loadData = async () => {
        try {
            let logUrl = 'logs/';
            // Only filter by single date if in 'today' mode. 
            // If history, we load more (backend usually returns last 30 days by default, or you can add params)
            if (logTab === 'today') logUrl += `?date=${filterDate}`;
            
            const [logRes, permRes, safeRes] = await Promise.all([
                api.get(logUrl),
                api.get('approvals/todo/'),
                api.get('safety/?status=OPEN')
            ]);

            setStats({
                logs: logRes.data,
                permits: permRes.data,
                safety: safeRes.data
            });
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    // --- LOG LOGIC ---
    const filteredLogs = useMemo(() => {
        let data = [...stats.logs];
        
        // Filter by specific engineer (current user)
        if (filterMyLogs && currentUser) {
            data = data.filter(l => (l.engineer_name || '').toLowerCase() === currentUser.toLowerCase());
        }

        // Apply History Filters
        if (logTab === 'history') {
            if (historyFilter.company) {
                data = data.filter(l => l.company.toLowerCase().includes(historyFilter.company.toLowerCase()));
            }
            if (historyFilter.date_from) {
                data = data.filter(l => new Date(l.date) >= new Date(historyFilter.date_from));
            }
            if (historyFilter.date_to) {
                data = data.filter(l => new Date(l.date) <= new Date(historyFilter.date_to));
            }
        }
        return data;
    }, [stats.logs, filterMyLogs, historyFilter, logTab, currentUser]);

    const logSummary = useMemo(() => {
        const headcount = filteredLogs.reduce((sum, l) => sum + (parseInt(l.headcount) || 0), 0);
        const hours = filteredLogs.reduce((sum, l) => sum + ((parseInt(l.headcount)||0) * (parseFloat(l.shift_hours)||0)), 0);
        return { headcount, hours, count: filteredLogs.length };
    }, [filteredLogs]);

    const handleExportLogs = () => {
        if (filteredLogs.length === 0) return alert("No logs to export");
        const headers = ["ID", "Ref #", "Date", "Company", "Project", "Location", "Engineer", "Headcount", "Shift(Hrs)", "Notes"];
        const csvRows = [headers.join(',')];
        
        filteredLogs.forEach(row => {
            const clean = (t) => `"${(t||'').toString().replace(/"/g, '""')}"`;
            csvRows.push([
                row.id, 
                clean(row.display_id), 
                row.date, clean(row.company), clean(row.project_name), 
                clean(row.location_detail), clean(row.engineer_name), 
                row.headcount, row.shift_hours, clean(row.notes)
            ].join(','));
        });

        const url = window.URL.createObjectURL(new Blob([csvRows.join('\n')], { type: 'text/csv' }));
        const a = document.createElement('a'); a.href = url; a.download = `Site_Logs_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
    };

    // --- RENDER HELPERS ---
    const DashboardCard = ({ id, title, color, icon: Icon, summary, children }) => {
        const isExpanded = expandedSection === id;
        return (
            <div style={{marginBottom:'20px', background:THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, borderLeft:`5px solid ${color}`, overflow:'hidden'}}>
                <div onClick={() => setExpandedSection(isExpanded ? null : id)} style={{padding:'20px', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer', background: isExpanded ? 'rgba(255,255,255,0.02)' : 'transparent'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
                        <div style={{color: color, background: 'rgba(255,255,255,0.05)', padding:'10px', borderRadius:'8px'}}>
                            <Icon size={24} />
                        </div>
                        <div>
                            <h3 style={{margin:0, color:'white', fontSize:'1.1rem'}}>{title}</h3>
                            <div style={{color:THEME.textSec, fontSize:'0.85rem', marginTop:'4px'}}>{summary}</div>
                        </div>
                    </div>
                    <div style={{color:THEME.textSec}}>
                        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </div>
                </div>
                {isExpanded && <div style={{borderTop:`1px solid ${THEME.border}`, padding:'20px', background:'#0f172a'}}>{children}</div>}
            </div>
        );
    };

    if (loading) return <div style={{color:'white'}}>Loading...</div>;

    return (
        <div className="animate-fade-in">
            {/* 1. DAILY ACTIVITY LOGS */}
            <DashboardCard 
                id="logs" 
                title="Daily Site Activity" 
                color={THEME.primary} 
                icon={ClipboardList}
                summary={`${logSummary.headcount} Workers • ${logSummary.hours.toFixed(1)} Man-Hours`}
            >
                {/* LOG TOOLBAR */}
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px', flexWrap:'wrap', gap:'10px'}}>
                    
                    {/* View Tabs */}
                    <div style={{display:'flex', gap:'5px', background:THEME.card, padding:'4px', borderRadius:'6px', border:`1px solid ${THEME.border}`}}>
                        <button onClick={() => setLogTab('today')} style={{padding:'6px 12px', borderRadius:'4px', border:'none', cursor:'pointer', background: logTab==='today'?THEME.primary:'transparent', color: logTab==='today'?'white':THEME.textSec, fontWeight:500}}>Today</button>
                        <button onClick={() => setLogTab('history')} style={{padding:'6px 12px', borderRadius:'4px', border:'none', cursor:'pointer', background: logTab==='history'?THEME.primary:'transparent', color: logTab==='history'?'white':THEME.textSec, fontWeight:500}}>History</button>
                    </div>
                    
                    {logTab === 'today' && (
                        <div style={{display:'flex', gap:'10px', alignItems:'center', flexWrap: 'wrap'}}>
                            <div style={{position:'relative', display:'flex', alignItems:'center'}}>
                                <Calendar size={16} style={{position:'absolute', left:'10px', color:THEME.textSec}}/>
                                <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={{padding:'6px 10px 6px 35px', borderRadius:'4px', border:`1px solid ${THEME.border}`, background:THEME.bg, color:'white'}} />
                            </div>
                            
                            <div 
                                onClick={() => setFilterMyLogs(!filterMyLogs)} 
                                style={{
                                    padding:'6px 12px', borderRadius:'20px', cursor:'pointer', fontSize:'0.85rem', fontWeight: 600, userSelect:'none', whiteSpace:'nowrap',
                                    border: filterMyLogs ? `1px solid ${THEME.primary}` : `1px solid ${THEME.border}`, 
                                    background: filterMyLogs ? 'rgba(59, 130, 246, 0.2)' : 'transparent', 
                                    color: filterMyLogs ? THEME.primary : THEME.textSec,
                                    display: 'flex', alignItems: 'center', gap: '6px'
                                }}
                            >
                                {filterMyLogs && <Check size={14} />} My Logs Only
                            </div>
                        </div>
                    )}
                    
                    {/* 🔥 FIXED HISTORY FILTERS (DATE RANGE) */}
                    {logTab === 'history' && (
                        <div style={{display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center'}}>
                            <input 
                                placeholder="Filter Company..." 
                                value={historyFilter.company} 
                                onChange={e => setHistoryFilter({...historyFilter, company: e.target.value})} 
                                style={{padding:'6px', borderRadius:'4px', border:`1px solid ${THEME.border}`, background:THEME.bg, color:'white', width:'140px'}} 
                            />
                            
                            <div style={{display:'flex', alignItems:'center', gap:'5px', background:THEME.bg, padding:'4px 8px', borderRadius:'4px', border:`1px solid ${THEME.border}`}}>
                                <span style={{fontSize:'0.75rem', color:THEME.textSec}}>From:</span>
                                <input 
                                    type="date" 
                                    value={historyFilter.date_from} 
                                    onChange={e => setHistoryFilter({...historyFilter, date_from: e.target.value})} 
                                    style={{border:'none', background:'transparent', color:'white', outline:'none', fontSize:'0.85rem'}} 
                                />
                            </div>

                            <div style={{display:'flex', alignItems:'center', gap:'5px', background:THEME.bg, padding:'4px 8px', borderRadius:'4px', border:`1px solid ${THEME.border}`}}>
                                <span style={{fontSize:'0.75rem', color:THEME.textSec}}>To:</span>
                                <input 
                                    type="date" 
                                    value={historyFilter.date_to} 
                                    onChange={e => setHistoryFilter({...historyFilter, date_to: e.target.value})} 
                                    style={{border:'none', background:'transparent', color:'white', outline:'none', fontSize:'0.85rem'}} 
                                />
                            </div>
                        </div>
                    )}

                    <button onClick={handleExportLogs} style={{background:THEME.success, color:'white', border:'none', padding:'6px 16px', borderRadius:'4px', cursor:'pointer', fontWeight:'bold', display:'flex', alignItems:'center', gap:'8px'}}>
                        <Download size={16} /> CSV
                    </button>
                </div>

                {/* LOG TABLE */}
                <div style={{overflowX: 'auto'}}>
                    <table style={{width:'100%', minWidth:'800px', textAlign:'left', color:THEME.textSec, fontSize:'0.9rem', borderCollapse:'collapse'}}>
                        <thead>
                            <tr style={{borderBottom:`1px solid ${THEME.border}`, color:THEME.primary}}>
                                <th style={{padding:'10px'}}>Ref #</th>
                                <th style={{padding:'10px'}}>Date</th>
                                <th>Company</th>
                                <th>Location</th>
                                {/* 🔥 ADDED RESPONSIBLE ENGINEER COLUMN */}
                                <th>Resp. Engineer</th> 
                                <th>Headcount</th>
                                <th>Shift</th>
                                <th>Permits</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLogs.map(l => (
                                <React.Fragment key={l.id}>
                                    <tr 
                                        onClick={() => setSelectedLog(selectedLog === l.id ? null : l.id)} 
                                        style={{borderBottom:`1px solid ${THEME.border}`, cursor:'pointer', background: selectedLog === l.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent'}}
                                    >
                                        <td style={{padding:'10px', color:THEME.primary, fontWeight:'bold'}}>{l.display_id || '#'+l.id}</td>
                                        <td style={{padding:'10px', color:'white'}}>{l.date}</td>
                                        <td style={{fontWeight:'bold', color: THEME.warning}}>{l.company}</td>
                                        <td>{l.location_detail}</td>
                                        {/* 🔥 ADDED ENGINEER DATA */}
                                        <td style={{color: 'white', fontWeight:'500'}}>{l.engineer_name || '-'}</td>
                                        <td style={{fontWeight:'bold', color:'white', display:'flex', alignItems:'center', gap:'5px', marginTop:'2px'}}><Users size={14}/> {l.headcount}</td>
                                        <td>{l.shift_hours}h</td>
                                        <td>
                                            {l.permits && l.permits.length > 0 ? 
                                                <span style={{background:THEME.card, padding:'2px 8px', borderRadius:'4px', border:`1px solid ${THEME.border}`, fontSize:'0.75rem', display:'inline-flex', alignItems:'center', gap:'4px'}}>
                                                    <LinkIcon size={12}/> {l.permits.length}
                                                </span> 
                                                : '-'}
                                        </td>
                                    </tr>
                                    {selectedLog === l.id && (
                                        <tr style={{background:THEME.card, borderBottom:`2px solid ${THEME.primary}`}}>
                                            <td colSpan="8" style={{padding:'20px'}}>
                                                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap:'20px'}}>
                                                    <div>
                                                        <h4 style={{marginTop:0, color:'white', display:'flex', alignItems:'center', gap:'8px'}}>
                                                            <FileText size={18} color={THEME.primary}/> Work Description
                                                        </h4>
                                                        <div style={{whiteSpace:'pre-wrap', color:THEME.textSec, paddingLeft:'26px'}}>{l.notes}</div>
                                                        
                                                        {l.permits && l.permits.length > 0 && (
                                                            <div style={{marginTop:'20px'}}>
                                                                <h4 style={{marginTop:0, color:'white', display:'flex', alignItems:'center', gap:'8px'}}>
                                                                    <FileSignature size={18} color={THEME.warning}/> Linked Permits
                                                                </h4>
                                                                <div style={{display:'flex', gap:'10px', flexWrap:'wrap', paddingLeft:'26px'}}>
                                                                    {l.permits.map(p => (
                                                                        <button 
                                                                            key={p.id}
                                                                            onClick={() => onNavigate('permits', p.id)}
                                                                            style={{
                                                                                padding:'6px 12px', 
                                                                                borderRadius:'6px', 
                                                                                border: `1px solid ${THEME.warning}`, 
                                                                                background: 'rgba(250, 204, 21, 0.1)', 
                                                                                color: THEME.warning, 
                                                                                cursor:'pointer', 
                                                                                fontSize:'0.85rem',
                                                                                fontWeight: 'bold',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '5px'
                                                                            }}
                                                                        >
                                                                            <LinkIcon size={14}/> {p.permit_type_name} {p.display_id || `#${p.id}`} ({p.status})
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        {/* Details Box */}
                                                        <div style={{background:'#0f172a', padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`, marginBottom:'15px'}}>
                                                            <h4 style={{marginTop:0, color:'white', display:'flex', alignItems:'center', gap:'8px'}}>
                                                                <UserCog size={18} color={THEME.primary}/> Assignment
                                                            </h4>
                                                            <div style={{fontSize:'0.9rem', color:THEME.textSec, marginBottom:'5px'}}><strong>Assigned To:</strong> {l.engineer_name || 'Unassigned'}</div>
                                                            <div style={{fontSize:'0.9rem', color:THEME.textSec}}><strong>Project:</strong> {l.project_name}</div>
                                                        </div>

                                                        <h4 style={{marginTop:0, color:'white', display:'flex', alignItems:'center', gap:'8px'}}>
                                                            <Users size={18} color={THEME.success}/> Register
                                                        </h4>
                                                        {l.worker_manifest && l.worker_manifest.length > 0 ? (
                                                            <div style={{display:'flex', flexWrap:'wrap', gap:'5px', paddingLeft:'26px'}}>
                                                                {l.worker_manifest.map((w, idx) => (
                                                                    <span key={idx} style={{background:'#0f172a', padding:'4px 10px', borderRadius:'12px', fontSize:'0.75rem', border:`1px solid ${THEME.border}`, color:'white'}}>{w.name}</span>
                                                                ))}
                                                            </div>
                                                        ) : <span style={{color:THEME.textSec, fontStyle:'italic', paddingLeft:'26px'}}>No register data.</span>}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
                {filteredLogs.length === 0 && <div style={{padding:'30px', textAlign:'center', color:THEME.textSec, fontStyle:'italic'}}>No logs matching your filters.</div>}
            </DashboardCard>

            {/* 2. PERMITS CARD (UNCHANGED) */}
            <DashboardCard 
                id="permits" 
                title="Permit Actions" 
                color={THEME.warning} 
                icon={FileSignature}
                summary={`${stats.permits.length} Permits Pending Approval`}
            >
                {stats.permits.length === 0 ? (
                    <div style={{color:THEME.success, display:'flex', alignItems:'center', gap:'10px'}}>
                        <CheckCircle2 size={20} /> All clear. No pending permits.
                    </div>
                ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                        {stats.permits.map(p => (
                            <div key={p.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', background:THEME.card, padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                                <div>
                                    <div style={{fontWeight:'bold', color:'white', display:'flex', alignItems:'center', gap:'8px'}}>
                                        <AlertTriangle size={16} color={THEME.warning}/> {p.permit_type_details?.name} {p.display_id || `#${p.id}`}
                                    </div>
                                    <div style={{fontSize:'0.85rem', color:THEME.textSec, marginLeft:'24px'}}>{p.company_name} • {p.location_label}</div>
                                </div>
                                <button onClick={() => onNavigate('permits', p.id)} style={{padding:'8px 16px', background:THEME.warning, color:'black', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer', fontSize:'0.85rem'}}>Review</button>
                            </div>
                        ))}
                    </div>
                )}
            </DashboardCard>

            {/* 3. SAFETY CARD (UNCHANGED) */}
            <DashboardCard 
                id="safety" 
                title="Safety Incidents" 
                color={THEME.danger} 
                icon={ShieldAlert}
                summary={`${stats.safety.length} Open Reports`}
            >
                {stats.safety.length === 0 ? (
                    <div style={{color:THEME.success, display:'flex', alignItems:'center', gap:'10px'}}>
                        <CheckCircle2 size={20} /> No open safety incidents.
                    </div>
                ) : (
                    <div style={{overflowX: 'auto'}}>
                        <table style={{width:'100%', minWidth:'500px', textAlign:'left', color:THEME.textSec, fontSize:'0.9rem'}}>
                            <thead><tr style={{color:THEME.danger}}><th>ID</th><th>Type</th><th>Location</th><th>Severity</th><th>Action</th></tr></thead>
                            <tbody>
                                {stats.safety.map(r => (
                                    <tr key={r.id} style={{borderBottom: `1px solid ${THEME.border}`}}>
                                        <td style={{padding:'12px 0', color:'white', fontWeight:'bold'}}>{r.display_id || '#'+r.id}</td>
                                        <td>{r.report_type}</td>
                                        <td>{r.location}</td>
                                        <td>
                                            <span style={{
                                                padding:'2px 8px', borderRadius:'4px', fontSize:'0.75rem', fontWeight:'bold',
                                                background: r.severity==='CRITICAL' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(250, 204, 21, 0.2)',
                                                color: r.severity==='CRITICAL' ? THEME.danger : THEME.warning
                                            }}>
                                                {r.severity}
                                            </span>
                                        </td>
                                        <td>
                                            <button 
                                                onClick={() => onNavigate('safety', r.id)} 
                                                style={{background:'transparent', border:`1px solid ${THEME.danger}`, color:THEME.danger, borderRadius:'4px', padding:'4px 12px', cursor:'pointer', fontSize:'0.75rem', fontWeight:'bold'}}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </DashboardCard>
        </div>
    );
};

export default OwnerOverview;